import { useContext, useState } from 'react'
import {
  Popover,
  styled,
  Typography,
  Stack,
  Divider,
  Box,
  Tooltip,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Avatar,
} from '@mui/material'
import { ThemeContext } from 'src/theme/ThemeProvider'
import CheckTwoToneIcon from '@mui/icons-material/CheckTwoTone'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import AccountCircleTwoToneIcon from '@mui/icons-material/AccountCircleTwoTone'
import PaletteTwoToneIcon from '@mui/icons-material/PaletteTwoTone'
import ExitToAppTwoToneIcon from '@mui/icons-material/ExitToAppTwoTone'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import useAuth from 'src/hooks/useAuth'
import defaultAvatar from 'src/assets/avatar.png'

const IconButtonWrapper = styled(IconButton)(
  ({ theme }) => `
  width: ${theme.spacing(4)};
  height: ${theme.spacing(4)};
  border-radius: ${theme.general.borderRadiusLg};
`
)

const ThemeToggleWrapper = styled(Box)(
  ({ theme }) => `
          padding: ${theme.spacing(1, 2)};
  `
)

const ButtonWrapper = styled(Box)(
  ({ theme }) => `
        cursor: pointer;
        position: relative;
        border-radius: ${theme.general.borderRadiusXl};
        padding: ${theme.spacing(0.8)};
        display: flex;
        flex-direction: row;
        align-items: stretch;
        min-width: 80px;
        box-shadow: 0 0 0 2px ${theme.colors.primary.lighter};

        &:hover {
            box-shadow: 0 0 0 2px ${theme.colors.primary.light};
        }

        &.active {
            box-shadow: 0 0 0 2px ${theme.palette.primary.main};

            .colorSchemeWrapper {
                opacity: .6;
            }
        }
  `
)

const ColorSchemeWrapper = styled(Box)(
  ({ theme }) => `

    position: relative;

    border-radius: ${theme.general.borderRadiusXl};
    height: 28px;
    
    &.colorSchemeWrapper {
        display: flex;
        align-items: stretch;
        width: 100%;

        .primary {
            border-top-left-radius: ${theme.general.borderRadiusXl};
            border-bottom-left-radius: ${theme.general.borderRadiusXl};
        }

        .secondary {
            border-top-right-radius: ${theme.general.borderRadiusXl};
            border-bottom-right-radius: ${theme.general.borderRadiusXl};
        }

        .primary,
        .secondary,
        .alternate {
            flex: 1;
        }
    }

    &.pureLight {
        .primary {
            background: #5569ff;
        }
    
        .secondary {
            background: #f2f5f9;
        }
    }

    &.greyGoose {
        .primary {
            background: #2442AF;
        }
    
        .secondary {
            background: #F8F8F8;
        }
    }
    
    &.purpleFlow {
        .primary {
            background: #9b52e1;
        }
    
        .secondary {
            background: #00b795;
        }
    }
    
    &.nebulaFighter {
        .primary {
            background: #8C7CF0;
        }
    
        .secondary {
            background: #070C27;
        }
    }

    &.greenFields {
        .primary {
            background: #44a574;
        }
    
        .secondary {
            background: #141c23;
        }
    }

    &.darkSpaces {
        .primary {
            background: #CB3C1D;
        }
    
        .secondary {
            background: #1C1C1C;
        }
    }

  `
)

const CheckSelected = styled(Box)(
  ({ theme }) => `
    background: ${theme.palette.success.main};
    border-radius: 50px;
    height: 26px;
    width: 26px;
    color: ${theme.palette.success.contrastText};
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: 50%;
    top: 50%;
    margin: -13px 0 0 -13px;
    z-index: 5;

    .MuiSvgIcon-root {
        height: 16px;
        width: 16px;
    }

  `
)

const ThemeSettings = () => {
  const { t } = useTranslation()
  const [settingsAnchor, setSettingsAnchor] = useState(null)
  const [showTheme, setShowTheme] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const handleLogout = async () => {
    try {
      await logout()
      navigate(location.pathname.startsWith('/minipay') ? '/minipay' : '/')
    } catch (err) {
      console.error(err)
    }
  }

  const handleOpen = (event) => {
    setSettingsAnchor(event.currentTarget)
  }

  const handleClose = () => {
    setSettingsAnchor(null)
    setShowTheme(false)
  }

  const setThemeName = useContext(ThemeContext)
  const curThemeName = localStorage.getItem('appTheme') || 'GreenFieldsTheme'

  const [theme, setTheme] = useState(curThemeName)

  if (!user) return null;

  const changeTheme = (theme) => {
    setTheme(theme)
    setThemeName(theme)
  }

  return (
    <>
      {/* <ThemeSettingsButton> */}
      <Tooltip arrow title={t('Configurações')}>
        <IconButtonWrapper
          onClick={handleOpen}
          color="primary"
          aria-label="settings"
          sx={{
            width: 'auto',
            height: 'auto',
            px: 0.5,
            gap: 0.5,
          }}
        >
          <Avatar
            alt={user?.name}
            src={user?.avatar || defaultAvatar}
            imgProps={{ onError: (e) => { e.target.src = defaultAvatar } }}
            sx={{ width: 32, height: 32 }}
          />
          <KeyboardArrowDownIcon fontSize="small" />
        </IconButtonWrapper>
      </Tooltip>
      <Popover
        disableScrollLock
        anchorEl={settingsAnchor}
        open={Boolean(settingsAnchor)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          popper: {
            modifiers: [
              { name: 'flip', enabled: false },
              { name: 'preventOverflow', options: { altAxis: false } },
            ],
          },
        }}
        PaperProps={{ sx: { width: 300 } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
          }}
        >
          <Avatar
            alt={user?.name}
            src={user?.avatar || defaultAvatar}
            imgProps={{ onError: (e) => { e.target.src = defaultAvatar } }}
            sx={{ width: 32, height: 32 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" noWrap sx={{ fontWeight: 'bold' }}>
              {user?.name}
            </Typography>
            <Typography variant="body2" noWrap color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <List disablePadding sx={{ width: '100%' }}>
          <ListItemButton
            onClick={() => {
              navigate('/extended-sidebar/menu/details')
              handleClose()
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <AccountCircleTwoToneIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Conta" />
          </ListItemButton>
          <ListItemButton onClick={() => setShowTheme((s) => !s)}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <PaletteTwoToneIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Tema" />
            {showTheme ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </ListItemButton>
          <Divider />
          <ListItemButton onClick={() => { handleClose(); setConfirmOpen(true) }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <ExitToAppTwoToneIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Sair" />
          </ListItemButton>
        </List>
        <Collapse in={showTheme} timeout="auto" unmountOnExit>
        <Divider />
        <Stack direction="column">

          <ThemeToggleWrapper>
            <Typography
              sx={{
                mt: 1,
                mb: 1,
                textAlign: 'center',
                fontWeight: 'bold',
                textTransform: 'uppercase',
              }}
              variant="body1"
            >
              Temas Claros
            </Typography>
            <Stack direction="row" justifyContent="center" spacing={2} sx={{ mb: 1 }}>
              <Tooltip placement="left" title="Pure Light" arrow>
                <ButtonWrapper
                  className={theme === 'PureLightTheme' ? 'active' : ''}
                  onClick={() => {
                    changeTheme('PureLightTheme')
                  }}
                >
                  {theme === 'PureLightTheme' && (
                    <CheckSelected>
                      <CheckTwoToneIcon />
                    </CheckSelected>
                  )}
                  <ColorSchemeWrapper className="colorSchemeWrapper pureLight">
                    <Box className="primary" />
                    <Box className="secondary" />
                  </ColorSchemeWrapper>
                </ButtonWrapper>
              </Tooltip>
              <Tooltip placement="left" title="Grey Goose" arrow>
                <ButtonWrapper
                  className={theme === 'GreyGooseTheme' ? 'active' : ''}
                  onClick={() => {
                    changeTheme('GreyGooseTheme')
                  }}
                >
                  {theme === 'GreyGooseTheme' && (
                    <CheckSelected>
                      <CheckTwoToneIcon />
                    </CheckSelected>
                  )}
                  <ColorSchemeWrapper className="colorSchemeWrapper greyGoose">
                    <Box className="primary" />
                    <Box className="secondary" />
                  </ColorSchemeWrapper>
                </ButtonWrapper>
              </Tooltip>
              <Tooltip placement="left" title="Purple Flow" arrow>
                <ButtonWrapper
                  className={theme === 'PurpleFlowTheme' ? 'active' : ''}
                  onClick={() => {
                    changeTheme('PurpleFlowTheme')
                  }}
                >
                  {theme === 'PurpleFlowTheme' && (
                    <CheckSelected>
                      <CheckTwoToneIcon />
                    </CheckSelected>
                  )}
                  <ColorSchemeWrapper className="colorSchemeWrapper purpleFlow">
                    <Box className="primary" />
                    <Box className="secondary" />
                  </ColorSchemeWrapper>
                </ButtonWrapper>
              </Tooltip>
            </Stack>
          </ThemeToggleWrapper>
          <ThemeToggleWrapper>
            <Typography
              sx={{
                mt: 1,
                mb: 1,
                textAlign: 'center',
                fontWeight: 'bold',
                textTransform: 'uppercase',
              }}
              variant="body1"
            >
              Temas Escuros
            </Typography>
            <Stack direction="row" justifyContent="center" spacing={2} sx={{ mb: 1 }}>
              <Tooltip placement="left" title="Nebula Fighter" arrow>
                <ButtonWrapper
                  className={theme === 'NebulaFighterTheme' ? 'active' : ''}
                  onClick={() => {
                    changeTheme('NebulaFighterTheme')
                  }}
                >
                  {theme === 'NebulaFighterTheme' && (
                    <CheckSelected>
                      <CheckTwoToneIcon />
                    </CheckSelected>
                  )}
                  <ColorSchemeWrapper className="colorSchemeWrapper nebulaFighter">
                    <Box className="primary" />
                    <Box className="secondary" />
                  </ColorSchemeWrapper>
                </ButtonWrapper>
              </Tooltip>
              <Tooltip placement="left" title="Green Fields" arrow>
                <ButtonWrapper
                  className={theme === 'GreenFieldsTheme' ? 'active' : ''}
                  onClick={() => {
                    changeTheme('GreenFieldsTheme')
                  }}
                >
                  {theme === 'GreenFieldsTheme' && (
                    <CheckSelected>
                      <CheckTwoToneIcon />
                    </CheckSelected>
                  )}
                  <ColorSchemeWrapper className="colorSchemeWrapper greenFields">
                    <Box className="primary" />
                    <Box className="secondary" />
                  </ColorSchemeWrapper>
                </ButtonWrapper>
              </Tooltip>
              <Tooltip placement="left" title="Dark Spaces" arrow>
                <ButtonWrapper
                  className={theme === 'DarkSpacesTheme' ? 'active' : ''}
                  onClick={() => {
                    changeTheme('DarkSpacesTheme')
                  }}
                >
                  {theme === 'DarkSpacesTheme' && (
                    <CheckSelected>
                      <CheckTwoToneIcon />
                    </CheckSelected>
                  )}
                  <ColorSchemeWrapper className="colorSchemeWrapper darkSpaces">
                    <Box className="primary" />
                    <Box className="secondary" />
                  </ColorSchemeWrapper>
                </ButtonWrapper>
              </Tooltip>
            </Stack>
          </ThemeToggleWrapper>
        </Stack>
        </Collapse>
      </Popover>
      {/* </ThemeSettingsButton> */}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirmar saída</DialogTitle>
        <DialogContent>
          <DialogContentText>Tem certeza que deseja sair?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button onClick={handleLogout} color="error" variant="contained">
            Sair
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ThemeSettings
